import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { EmailPreview } from '@/components/email/EmailPreview';

// Pre-send confirmation modals for the medical-staff invite / resend flows. The email body is the
// BE single-owner render (GET /admin/email-previews/staff-invite), personalized with the entered
// form values so the operator sees the exact mail that will be sent — NOT a hand-built second
// template (which is what let the old card drift from the real mail). The shared body is exported
// for the resend variant to reuse.

const I18N = 'medicalStaffManagement.invitePreviewModal';

interface InvitePreviewBodyProps {
  name: string;
  email: string;
  // Already-translated display labels (e.g. 신경과 / CRC), passed to the server render as-is.
  departmentLabel: string;
  jobRoleLabel: string;
  // Send-time chrome string (YYYY-MM-DD HH:mm), built by the caller.
  sentAt: string;
  // Renders the resend banner (server-side) instead of a first-time invite.
  resend?: boolean;
}

/** The email-body preview shared by the invite and resend modals — the BE render, not a copy. */
export function InvitePreviewBody({
  name,
  email,
  departmentLabel,
  jobRoleLabel,
  sentAt,
  resend = false,
}: InvitePreviewBodyProps) {
  // SHCT-651 — the 발신자/발송일시/받는사람 box used to be painted HERE, on top of the server render,
  // which already emits that exact box (`_layout.html :: page`). Two boxes on screen. The entered
  // values now ride along as vars so the ONE server-rendered box carries them.
  return (
    <EmailPreview
      kind="staff-invite"
      resend={resend}
      vars={{
        name,
        department: departmentLabel,
        jobRole: jobRoleLabel,
        recipientEmail: email,
        sentAt,
      }}
    />
  );
}

interface MedicalStaffInvitePreviewModalProps {
  open: boolean;
  name: string;
  email: string;
  departmentLabel: string;
  jobRoleLabel: string;
  sentAt: string;
  submitting: boolean;
  // [← 수정] — close the preview and return to the form.
  onEdit: () => void;
  // [✉ 초대 발송] — run the actual invite.
  onConfirm: () => void;
}

export function MedicalStaffInvitePreviewModal({
  open,
  name,
  email,
  departmentLabel,
  jobRoleLabel,
  sentAt,
  submitting,
  onEdit,
  onConfirm,
}: MedicalStaffInvitePreviewModalProps) {
  const { t } = useTranslation('admin');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onEdit]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={onEdit}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal"
        style={{
          maxWidth: 640,
          width: '92vw',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 className="modal__title">{t(`${I18N}.title`)}</h2>
        </div>
        <div className="modal__body" style={{ overflowY: 'auto' }}>
          <InvitePreviewBody
            name={name}
            email={email}
            departmentLabel={departmentLabel}
            jobRoleLabel={jobRoleLabel}
            sentAt={sentAt}
          />
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onEdit}
            disabled={submitting}
          >
            {t(`${I18N}.editButton`)}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onConfirm}
            disabled={submitting}
          >
            {t(`${I18N}.sendButton`)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
